import React, { useState, useEffect, useCallback } from "react";
import { withTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import {
  Map,
  Marker,
  InfoWindow,
  Polyline,
  GoogleApiWrapper,
} from "google-maps-react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { format, startOfDay, endOfDay } from "date-fns";
import MDSpinner from "react-md-spinner";
import { applyGradualMapZoom } from "../../utils/gradualMapZoom";

import myConfig from "../../configs/config";
import AttendanceInfoWindow from "./Components/AttendanceInfoWindow";
import SelectBox from "../../components/SelectBox";
import { buildUtcFromToParams } from "../../helpers/utils";
import MapIconColorLegend from "../../components/Dashboard/MapIconColorLegend";
import { resolveEquipmentMapIconUrl } from "../../utils/mapMarkerIcons";

import "./AttendanceMap.css";
import "../../components/Dashboard/Marker.css";

function AttendanceMap(props) {
  const params = useParams();
  const location = useLocation();
  const farmId = params.farm_id;

  // Get worker ID from query params if present
  const queryParams = new URLSearchParams(location.search);
  const initialWorkerId = queryParams.get("worker");

  const [workers, setWorkers] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [activityFilter, setActivityFilter] = useState("all");
  const [workerTrails, setWorkerTrails] = useState({});
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [mapCenter, setMapCenter] = useState({ lat: -15.7942, lng: -47.8822 }); // Brasília default
  const mapZoom = 12;

  // Fetch workers with profiles
  const fetchWorkers = useCallback(async () => {
    try {
      const WORKER_PROFILE_URL = `${myConfig.BE_URL}/api/attendance/worker-profiles/?farm_id=${farmId}&is_active=true`;

      const response = await axios({
        baseURL: WORKER_PROFILE_URL,
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      });

      const data = response.data;
      const workersList = Array.isArray(data) ? data : [];
      setWorkers(workersList);

      // Set selected worker from query param
      if (initialWorkerId && workersList.length > 0) {
        const worker = workersList.find(
          (w) => w.operator_id === parseInt(initialWorkerId)
        );
        if (worker) {
          setSelectedWorker(worker);
        }
      }
    } catch (error) {
      console.error("Error fetching workers:", error);
      toast.error(props.t("Falha ao carregar trabalhadores"), {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  }, [farmId, initialWorkerId, props]);

  // Fetch positions for today
  const fetchPositions = useCallback(async () => {
    try {
      const today = new Date();
      const { from: fromDate, to: toDate } = buildUtcFromToParams(
        startOfDay(today),
        endOfDay(today),
      );

      const POSITIONS_URL = `${myConfig.BE_URL}/api/operator/positions/?farm_id=${farmId}&from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}&limit=1000`;

      const response = await axios({
        baseURL: POSITIONS_URL,
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      });

      const data = response.data;
      const positionsList = data.positions || data.results || [];

      setPositions(Array.isArray(positionsList) ? positionsList : []);
      setLastUpdate(new Date());

      // Set map center to first position if available
      if (positionsList.length > 0) {
        setMapCenter({
          lat: positionsList[0].latitude,
          lng: positionsList[0].longitude,
        });
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching positions:", error);
      toast.error(props.t("Falha ao carregar posições"), {
        position: "bottom-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setLoading(false);
    }
  }, [farmId, props]);

  // Fetch trail for selected worker
  const fetchWorkerTrail = useCallback(async (operatorId) => {
    try {
      const today = new Date();
      const { from: fromDate, to: toDate } = buildUtcFromToParams(
        startOfDay(today),
        endOfDay(today),
      );

      const POSITIONS_URL = `${myConfig.BE_URL}/api/operator/positions/?operator_id=${operatorId}&from=${encodeURIComponent(fromDate)}&to=${encodeURIComponent(toDate)}&limit=500`;

      const response = await axios({
        baseURL: POSITIONS_URL,
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("client-token")}`,
        },
      });

      const data = response.data;
      const trail = data.positions || data.results || [];

      setWorkerTrails((prev) => ({
        ...prev,
        [operatorId]: Array.isArray(trail) ? trail : [],
      }));
    } catch (error) {
      console.error("Error fetching worker trail:", error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    document.title = "Agriconnected - Mapa de Presença";
    fetchWorkers();
  }, [fetchWorkers]);

  useEffect(() => {
    if (workers.length > 0) {
      fetchPositions();
    }
  }, [workers, fetchPositions]);

  // 30-second polling
  useEffect(() => {
    const interval = setInterval(() => {
      fetchPositions();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [fetchPositions]);

  // Fetch trail when worker is selected
  useEffect(() => {
    if (selectedWorker && selectedWorker.operator_id) {
      fetchWorkerTrail(selectedWorker.operator_id);
    }
  }, [selectedWorker, fetchWorkerTrail]);

  // Get last position for each operator
  const getLastPositionForOperator = (operatorId) => {
    const operatorPositions = positions
      .filter((p) => p.operator === operatorId)
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    return operatorPositions.length > 0 ? operatorPositions[0] : null;
  };

  const getMarkerIcon = (worker, position) => {
    if (!position || !props.google || !props.google.maps) return null;
    const url = resolveEquipmentMapIconUrl({
      traccar_ico: "person",
      iconKind: "pedestrian",
      assumed: true,
      speed: position.speed,
      lastMovedAt: position.timestamp,
    });
    return {
      url,
      scaledSize: new props.google.maps.Size(36, 52),
      anchor: new props.google.maps.Point(18, 52),
    };
  };

  // Filter workers based on status and activity
  const getFilteredWorkers = () => {
    return workers.filter((worker) => {
      const position = getLastPositionForOperator(worker.operator_id);
      if (!position) return false;

      // Activity filter
      if (activityFilter !== "all") {
        if (position.activity_type !== activityFilter) return false;
      }

      return true;
    });
  };

  const filteredWorkers = getFilteredWorkers();

  // Handle marker click
  const onMarkerClick = (worker, position) => {
    setSelectedWorker(worker);
    setSelectedPosition(position);
    setShowInfoWindow(true);

    // Center map on marker
    setMapCenter({
      lat: position.latitude,
      lng: position.longitude,
    });
  };

  // Activity options for filter
  const activityOptions = [
    { id: "all", name: props.t("Todas as Atividades") },
    { id: "walking", name: props.t("Andando") },
    { id: "driving", name: props.t("Dirigindo") },
    { id: "still", name: props.t("Parado") },
  ];

  // Get trail coordinates for selected worker
  const getTrailCoordinates = () => {
    if (!selectedWorker || !selectedWorker.operator_id) return [];

    const trail = workerTrails[selectedWorker.operator_id] || [];
    return trail.map((pos) => ({
      lat: pos.latitude,
      lng: pos.longitude,
    }));
  };

  const trailCoordinates = getTrailCoordinates();

  return (
    <div id="attendance_map_container">
      <div className="map-header">
        <h2>{props.t("Mapa de Presença")}</h2>
        <div className="last-update-info">
          <span>
            {props.t("Última Atualização")}:{" "}
            {format(lastUpdate, "dd/MM/yyyy HH:mm:ss")}
          </span>
          <button
            className="btn btn-sm btn-primary"
            onClick={fetchPositions}
            style={{ marginLeft: "15px" }}
          >
            {props.t("Atualizar Agora")}
          </button>
        </div>
      </div>

      {loading ? (
        <div
          style={{ marginTop: "50px", marginLeft: "50%", minHeight: "200px" }}
        >
          <MDSpinner />
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="map-filters">
            <div className="filter-group">
              <label>{props.t("Filtrar por Atividade")}</label>
              <SelectBox
                data={activityOptions}
                selected_value={activityFilter}
                value="name"
                change={(e) => setActivityFilter(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>{props.t("Trabalhador Selecionado")}</label>
              <select
                className="form-control"
                value={selectedWorker ? selectedWorker.operator_id : ""}
                onChange={(e) => {
                  const worker = workers.find(
                    (w) => w.operator_id === parseInt(e.target.value)
                  );
                  setSelectedWorker(worker || null);
                }}
              >
                <option value="">{props.t("Nenhum selecionado")}</option>
                {workers.map((worker) => (
                  <option key={worker.operator_id} value={worker.operator_id}>
                    {worker.operator_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Map */}
          <div className="map-container">
            <MapIconColorLegend placement="top" />
            <Map
              google={props.google}
              zoom={mapZoom}
              center={mapCenter}
              initialCenter={mapCenter}
              style={{ width: "100%", height: "600px" }}
              mapTypeId={props.google.maps.MapTypeId.HYBRID}
              streetViewControl={false}
              fullscreenControl={true}
              mapTypeControl={true}
              zoomControl={false}
              onReady={(_props, map) => applyGradualMapZoom(map)}
            >
              {/* Render markers for all filtered workers */}
              {filteredWorkers.map((worker) => {
                const position = getLastPositionForOperator(worker.operator_id);
                if (!position) return null;

                return (
                  <Marker
                    key={worker.operator_id}
                    position={{
                      lat: position.latitude,
                      lng: position.longitude,
                    }}
                    icon={getMarkerIcon(worker, position)}
                    onClick={() => onMarkerClick(worker, position)}
                    title={worker.operator_name}
                  />
                );
              })}

              {/* Show InfoWindow for selected worker */}
              {showInfoWindow && selectedWorker && selectedPosition && (
                <InfoWindow
                  position={{
                    lat: selectedPosition.latitude,
                    lng: selectedPosition.longitude,
                  }}
                  visible={showInfoWindow}
                  onClose={() => setShowInfoWindow(false)}
                >
                  <AttendanceInfoWindow
                    worker={selectedWorker}
                    position={selectedPosition}
                    attendance={null}
                  />
                </InfoWindow>
              )}

              {/* Show trail for selected worker */}
              {selectedWorker && trailCoordinates.length > 1 && (
                <Polyline
                  path={trailCoordinates}
                  strokeColor="#007bff"
                  strokeOpacity={0.7}
                  strokeWeight={3}
                />
              )}
            </Map>
          </div>

        </>
      )}

      <ToastContainer />
    </div>
  );
}

export default GoogleApiWrapper({
  apiKey: myConfig.GOOGLE_API_KEY,
})(withTranslation()(AttendanceMap));
